import React from 'react';
import PropTypes from 'prop-types';

/**
 * SVGIcon component that handles SVGs properly in both client and server-side rendering
 * This component can handle both imported SVG files and raw SVG strings
 */
function SVGIcon({ svg, className, style, width, height, ...props }) {
  // Check if we're in a server-side rendering environment
  const isSSR = typeof window === 'undefined';

  // Helper function to extract SVG content from various module formats
  const extractSVGContent = svgModule => {
    if (!svgModule) return null;

    // Handle different webpack module formats
    if (typeof svgModule === 'string') {
      return svgModule;
    }

    if (typeof svgModule === 'object') {
      // Handle raw-loader output (most common case)
      if (svgModule.default && typeof svgModule.default === 'string') {
        return svgModule.default;
      }

      // Handle webpack 5 asset/source format
      if (svgModule.default && typeof svgModule.default === 'string') {
        return svgModule.default;
      }

      // Handle webpack 4 file-loader format (fallback)
      if (svgModule.default && typeof svgModule.default === 'string') {
        return svgModule.default;
      }

      // Handle direct SVG content
      if (svgModule.__esModule && svgModule.default) {
        return svgModule.default;
      }

      // Handle the specific webpack module export format that's causing issues
      // This is the case where we get: "module.exports = __webpack_public_path__ + 'filename.svg';"
      if (isSSR && svgModule.toString && typeof svgModule.toString === 'function') {
        const moduleString = svgModule.toString();
        if (
          moduleString.includes('module.exports') &&
          moduleString.includes('__webpack_public_path__')
        ) {
          // In SSR, we can't resolve this webpack path, so we need to handle it differently
          // For now, we'll return null and let the fallback handle it
          return null;
        }
      }
    }

    return null;
  };

  // Extract the actual SVG content
  const svgContent = extractSVGContent(svg);

  // If we have SVG content, render it
  if (svgContent) {
    return (
      <span
        className={className}
        style={style}
        dangerouslySetInnerHTML={{ __html: svgContent }}
        {...props}
      />
    );
  }

  // Fallback: render as a regular image if possible
  if (svg && typeof svg === 'string' && (svg.startsWith('data:image/') || svg.startsWith('http'))) {
    return (
      <img
        src={svg}
        className={className}
        style={style}
        width={width}
        height={height}
        alt=""
        {...props}
      />
    );
  }

  // If all else fails, render nothing and log a warning
  if (process.env.NODE_ENV !== 'production') {
    console.warn('SVGIcon: Unable to render SVG', {
      svg,
      type: typeof svg,
      keys: svg ? Object.keys(svg) : null,
      isSSR,
      toString: svg && typeof svg.toString === 'function' ? svg.toString() : null,
    });
  }

  return null;
}

SVGIcon.propTypes = {
  svg: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default SVGIcon;
